<script setup>
/**
 * WhatsappDeliveryWebhookForm.vue — form condiviso (admin + cliente) per il
 * webhook di notifica esiti delivery WhatsApp: abilitazione, URL, scope, secret.
 * Non conosce gli endpoint: riceve fetchConfig/saveConfig dal wrapper e lavora
 * su { enabled, url, scope, hasSecret, secret? }. Il secret è generato server-side
 * e il GET non lo restituisce mai (solo hasSecret): `secret` arriva solo nella
 * risposta del salvataggio che l'ha generato/rigenerato e viene mostrato una sola
 * volta. Rotazione opt-in al salvataggio.
 * I wrapper devono montarlo con v-if sulla visibilità: ogni apertura rimonta
 * il componente e ricarica lo stato aggiornato.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';

const { t } = useI18n();
const toast = useToast();

const props = defineProps({
  fetchConfig: { type: Function, required: true },
  saveConfig: { type: Function, required: true },
});
const emit = defineEmits(['saved', 'load-error']);

const enabled = ref(false);
const url = ref('');
const scope = ref('all');
const secret = ref(''); // in chiaro, solo subito dopo generazione/rigenerazione
const hasSecret = ref(false);
const regenerate = ref(false);
const copied = ref(false);
let copiedTimer = null;
const loading = ref(true);
const saving = ref(false);

const scopeOptions = computed(() => [
  { label: t('whatsapp.deliveryWebhook.scopeAll'), value: 'all' },
  { label: t('whatsapp.deliveryWebhook.scopeTemplate'), value: 'template' },
]);

function applyConfig(c) {
  enabled.value = Boolean(c?.enabled);
  url.value = c?.url || '';
  scope.value = c?.scope || 'all';
  secret.value = c?.secret || '';
  hasSecret.value = Boolean(c?.hasSecret) || Boolean(c?.secret);
  regenerate.value = false;
}

onMounted(async () => {
  try {
    applyConfig(await props.fetchConfig());
  } catch (err) {
    emit('load-error', err);
    // 404 significa "superficie non applicabile" (es. connettore non attivo/rimosso):
    // il contenitore nasconde la sezione, niente toast d'errore da mostrare all'utente.
    if (err?.response?.status !== 404) {
      toast.add({
        severity: 'error',
        summary: t('whatsapp.deliveryWebhook.loadError'),
        detail: err.response?.data?.error?.message || err.message,
        life: 5000,
      });
    }
  } finally {
    loading.value = false;
  }
});

const isValidUrl = () => {
  let parsed;
  try {
    parsed = new URL(url.value.trim());
  } catch {
    return false;
  }
  if (parsed.protocol === 'https:') return true;
  return parsed.protocol === 'http:' && (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1');
};
const canSave = () => !loading.value && (!enabled.value || isValidUrl());

const copySecret = async () => {
  try {
    await navigator.clipboard.writeText(secret.value);
    copied.value = true;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => { copied.value = false; }, 2000);
    toast.add({ severity: 'success', summary: t('whatsapp.deliveryWebhook.secretCopied'), life: 2000 });
  } catch {
    toast.add({ severity: 'error', summary: t('common.error'), life: 3000 });
  }
};

onBeforeUnmount(() => clearTimeout(copiedTimer));

const onSave = async () => {
  if (!canSave() || saving.value) return;
  saving.value = true;
  try {
    const updated = await props.saveConfig({
      enabled: enabled.value,
      url: url.value.trim(),
      scope: scope.value,
      regenerateSecret: regenerate.value,
    });
    if (updated) applyConfig(updated);
    toast.add({ severity: 'success', summary: t('whatsapp.deliveryWebhook.saved'), life: 3000 });
    emit('saved', updated || null);
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: t('whatsapp.deliveryWebhook.error'),
      detail: err.response?.data?.error?.message || err.message,
      life: 5000,
    });
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <!-- Skeleton che rispecchia le quattro righe del form: evita il salto di
       altezza quando arrivano i dati reali. -->
  <div v-if="loading" class="flex flex-col gap-4" aria-busy="true">
    <span class="sr-only">{{ t('common.loading') }}</span>
    <div class="flex items-center justify-between">
      <Skeleton width="10rem" height="1rem" />
      <Skeleton width="2.5rem" height="1.5rem" border-radius="9999px" />
    </div>
    <div class="flex flex-col gap-2">
      <Skeleton width="7rem" height="1rem" />
      <Skeleton width="100%" height="2.25rem" />
      <Skeleton width="75%" height="0.75rem" class="opacity-60" />
    </div>
    <div class="flex flex-col gap-2">
      <Skeleton width="8rem" height="1rem" />
      <Skeleton width="100%" height="2.25rem" />
    </div>
    <div class="flex flex-col gap-2">
      <Skeleton width="4rem" height="1rem" />
      <Skeleton width="100%" height="2.25rem" />
      <Skeleton width="50%" height="0.75rem" class="opacity-60" />
    </div>
  </div>
  <div v-else class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <label for="wdw-enabled" class="text-sm font-medium text-gray-300">{{ t('whatsapp.deliveryWebhook.enabledLabel') }}</label>
      <ToggleSwitch id="wdw-enabled" v-model="enabled" :disabled="saving" />
    </div>

    <div class="flex flex-col gap-1">
      <label for="wdw-url" class="text-sm font-medium text-gray-300">
        {{ t('whatsapp.deliveryWebhook.urlLabel') }} <span v-if="enabled" class="text-red-400" aria-hidden="true">*</span>
      </label>
      <InputText
        id="wdw-url"
        v-model="url"
        class="w-full"
        :disabled="!enabled || saving"
        :invalid="enabled && url !== '' && !isValidUrl()"
        :aria-required="enabled || undefined"
        aria-describedby="wdw-url-hint"
        placeholder="https://…"
      />
      <p id="wdw-url-hint" class="text-xs text-gray-400 mt-1">{{ t('whatsapp.deliveryWebhook.urlHint') }}</p>
    </div>

    <div class="flex flex-col gap-1">
      <label for="wdw-scope" class="text-sm font-medium text-gray-300">{{ t('whatsapp.deliveryWebhook.scopeLabel') }}</label>
      <Select
        id="wdw-scope"
        v-model="scope"
        :options="scopeOptions"
        option-label="label"
        option-value="value"
        class="w-full"
        :disabled="!enabled || saving"
      />
    </div>

    <div class="flex flex-col gap-1">
      <!-- Prima del primo salvataggio il secret non esiste ancora: al posto di
           input vuoto e azioni disabilitate, spieghiamo quando verrà generato. -->
      <template v-if="hasSecret">
        <label for="wdw-secret" class="text-sm font-medium text-gray-300">{{ t('whatsapp.deliveryWebhook.secretLabel') }}</label>
        <template v-if="secret">
          <div class="flex gap-2">
            <InputText id="wdw-secret" :model-value="secret" readonly class="w-full input-mono" size="small" />
            <Button
              severity="secondary"
              :aria-label="t('whatsapp.deliveryWebhook.copySecret')"
              v-tooltip.top="t('whatsapp.deliveryWebhook.copySecret')"
              @click="copySecret"
            >
              <span class="material-icons" style="font-size: 16px" aria-hidden="true">{{ copied ? 'check' : 'content_copy' }}</span>
            </Button>
          </div>
          <Message severity="warn" size="small" :pt="{ root: { role: 'status', 'aria-live': 'polite' } }" class="mt-1">
            <template #icon><span class="material-icons select-none" style="font-size: 16px" aria-hidden="true">warning</span></template>
            {{ t('whatsapp.deliveryWebhook.secretShownOnce') }}
          </Message>
        </template>
        <span v-else class="inline-flex self-start items-center gap-1.5 px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-xs text-gray-400">
          •••••••• {{ t('common.secret.set') }}
        </span>
        <div class="flex items-center gap-2 mt-1">
          <Checkbox v-model="regenerate" input-id="wdw-regen" binary :disabled="saving" />
          <label for="wdw-regen" class="text-xs text-gray-400">{{ t('whatsapp.deliveryWebhook.regenerateSecret') }}</label>
        </div>
        <p class="text-xs text-gray-400 mt-1">{{ t('whatsapp.deliveryWebhook.secretHint') }}</p>
      </template>
      <template v-else>
        <span class="text-sm font-medium text-gray-300">{{ t('whatsapp.deliveryWebhook.secretLabel') }}</span>
        <p class="text-xs text-gray-400">{{ t('whatsapp.deliveryWebhook.secretPendingHint') }}</p>
      </template>
    </div>

    <div class="flex justify-end">
      <Button
        severity="primary"
        :label="saving ? t('common.saving') : t('whatsapp.deliveryWebhook.save')"
        :loading="saving"
        :disabled="saving || !canSave()"
        @click="onSave"
      />
    </div>
  </div>
</template>
